/**
 * `/settings/danger` — destructive workspace actions.
 *
 * Admin-only. Non-admins are bounced back to `/settings/profile`
 * with a toast. Two actions:
 *
 *   1. Leave workspace — any non-owner member.
 *      `membership.destroy()` → navigate to `/`.
 *   2. Delete workspace — typed-confirmation (user must type the
 *      org slug), then a final AlertDialog. `organization.destroy()`
 *      → navigate to `/`.
 *
 * The typed-confirmation step is its own `<Dialog>` rather than a
 * `<ConfirmDialog>` because the latter renders its `description`
 * into `AlertDialogPrimitive.Description` (which emits a `<p>`), and
 * nesting form controls inside a `<p>` is invalid HTML — the
 * browser breaks the layout around it.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toastFromError, toastInfo, toastSuccess } from '@/lib/errors';
import { useOrganization, useUser } from '@clerk/react';
import { Warning } from '@phosphor-icons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/danger')({
  component: DangerPage,
});

function DangerPage(): ReactElement {
  const { user, isLoaded: userLoaded } = useUser();
  const { organization, membership, isLoaded: orgLoaded } = useOrganization();
  const navigate = useNavigate();

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [typedOpen, setTypedOpen] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);
  const [typedSlug, setTypedSlug] = useState('');
  const [deleting, setDeleting] = useState(false);

  if (!userLoaded || !orgLoaded) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full max-w-xl" />
        <Skeleton className="h-24 w-full max-w-xl" />
      </div>
    );
  }

  if (!organization || !membership || !user) {
    return (
      <p className="text-sm text-[var(--color-mute)]">
        You need an active workspace to see danger-zone controls.
      </p>
    );
  }

  const isAdmin = membership.role === 'admin' || membership.role === 'org:admin';
  const slug = organization.slug ?? organization.name;

  if (!isAdmin) {
    queueMicrotask(() => {
      toastInfo('Admin-only', 'Only workspace admins can use the danger zone.');
      void navigate({ to: '/settings/profile', replace: true });
    });
    return <></>;
  }

  // -----------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------

  async function onLeaveConfirm() {
    if (!membership) return;
    setLeaving(true);
    try {
      await membership.destroy();
      toastSuccess('You left the workspace');
      await navigate({ to: '/', replace: true });
    } catch (err) {
      toastFromError(err);
    } finally {
      setLeaving(false);
      setLeaveOpen(false);
    }
  }

  function onTypedSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (typedSlug.trim() !== slug) {
      toastInfo('Slug mismatch', 'The text must match exactly.');
      return;
    }
    setTypedOpen(false);
    setFinalOpen(true);
  }

  async function onDeleteConfirm() {
    if (!organization) return;
    setDeleting(true);
    try {
      await organization.destroy();
      toastSuccess('Workspace deleted');
      await navigate({ to: '/', replace: true });
    } catch (err) {
      toastFromError(err);
    } finally {
      setDeleting(false);
      setFinalOpen(false);
      setTypedSlug('');
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-danger)]">
          <Warning aria-hidden="true" />
          Danger zone
        </h2>
        <p className="mt-1 text-sm text-[var(--color-mute)]">
          Irreversible workspace-level actions. Double-check before confirming.
        </p>
      </header>

      <DangerCard
        title="Leave workspace"
        description="You'll lose access to every audit, monitor, and API key in this workspace. You can be re-invited later."
        cta={
          <Button
            variant="destructive-outline"
            onClick={() => setLeaveOpen(true)}
            disabled={leaving}
          >
            Leave workspace
          </Button>
        }
      />

      <DangerCard
        title="Delete workspace"
        description="Permanently removes this workspace along with every audit, monitor, and API key it contains. This cannot be undone."
        cta={
          <Button
            variant="destructive"
            onClick={() => {
              setTypedSlug('');
              setTypedOpen(true);
            }}
            disabled={deleting}
          >
            Delete workspace…
          </Button>
        }
      />

      {/* Leave workspace — single confirm step (reversible via re-invite). */}
      <ConfirmDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        title="Leave this workspace?"
        description={`${organization.name}: you'll lose access until an admin invites you back.`}
        confirmLabel="Leave"
        destructive
        loading={leaving}
        onConfirm={onLeaveConfirm}
      />

      {/* Delete — step 1: typed slug confirmation (custom Dialog so the
          input + label render outside a <p>). */}
      <Dialog
        open={typedOpen}
        onOpenChange={(next) => {
          setTypedOpen(next);
          if (!next) setTypedSlug('');
        }}
      >
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle>Type the workspace slug to continue</DialogTitle>
            <DialogDescription>
              This permanently deletes {organization.name} and every audit, monitor, and API key it
              owns. There is no undo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onTypedSubmit} noValidate>
            <DialogPanel>
              <Field>
                <FieldLabel htmlFor="slug-confirm">
                  Type <span className="font-mono text-xs text-[var(--color-ink)]">{slug}</span> to
                  enable the delete button
                </FieldLabel>
                <Input
                  id="slug-confirm"
                  value={typedSlug}
                  onChange={(e) => setTypedSlug((e.target as HTMLInputElement).value)}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </Field>
            </DialogPanel>
            <DialogFooter>
              <DialogClose render={<Button variant="outline">Cancel</Button>} />
              <Button type="submit" variant="destructive" disabled={typedSlug.trim() !== slug}>
                Continue
              </Button>
            </DialogFooter>
          </form>
        </DialogPopup>
      </Dialog>

      {/* Delete — step 2: final AlertDialog confirm. */}
      <ConfirmDialog
        open={finalOpen}
        onOpenChange={(next) => {
          setFinalOpen(next);
          if (!next) setTypedSlug('');
        }}
        title="Delete workspace for real?"
        description={`${organization.name} and every record inside it will be removed immediately. This is your last chance to cancel.`}
        confirmLabel="Delete permanently"
        destructive
        loading={deleting}
        onConfirm={onDeleteConfirm}
      />
    </div>
  );
}

// -----------------------------------------------------------------
// DangerCard — title + description + CTA.
// Layout: stacks on mobile, horizontal on ≥sm with the CTA at the
// start (top-aligned — not centered) so long descriptions don't
// push the button off-grid.
// -----------------------------------------------------------------

function DangerCard({
  title,
  description,
  cta,
}: {
  title: string;
  description: string;
  cta: ReactElement;
}): ReactElement {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        <p className="mt-1 text-sm text-[var(--color-mute)]">{description}</p>
      </div>
      <div className="shrink-0 sm:pt-0.5">{cta}</div>
    </section>
  );
}
